/**
 * Paleta de fallback (orden estable) para tecnologías que no caen en ninguna familia
 * conocida. Se usa por índice para mantener consistencia entre renders.
 */
export const PALETA_TECNOLOGIAS_FALLBACK = [
  '#5d3fd3',
  '#26a69a',
  '#ff7043',
  '#8e24aa',
  '#7b8a3a',
  '#c2185b',
  '#0097a7',
  '#5d4037',
  '#43a047',
  '#fb8c00'
];

export const COLOR_FALLBACK_TECNOLOGIA = '#9e9e9e';

/**
 * Mapeo semántico tecnología → color por familias estándar del sector.
 * Solar amarillo, eólica verde, hidráulica azul, térmica gris, biomasa marrón, etc.
 *
 * Orden importante: la primera familia que matchee gana. Las palabras clave
 * más específicas (p. ej. `biogas`, `biomasa`) deben ir antes que las más
 * genéricas (`gas` → térmica) para evitar coloreos incorrectos.
 */
const FAMILIAS_TECNOLOGIA: Array<{ palabras: string[]; color: string }> = [
  { palabras: ['solar', 'fotovolt', 'fotov'], color: '#f5b800' },
  { palabras: ['eolic', 'viento', 'wind'], color: '#4caf50' },
  {
    palabras: [
      'minihidr',
      'mini hidr',
      'mini-hidr',
      'hidraul',
      'hidro de pasada',
      'hidro pasada',
      'hidro',
      'pasada',
      'agua'
    ],
    color: '#1976d2'
  },
  { palabras: ['biomasa', 'biogas', 'bio gas', 'bio-gas'], color: '#6d4c41' },
  { palabras: ['geoterm'], color: '#d35400' },
  { palabras: ['mareo', 'olas', 'oceano'], color: '#0097a7' },
  { palabras: ['cogener'], color: '#7b1fa2' },
  {
    palabras: [
      'termic',
      'termoelec',
      'combust',
      'diesel',
      'petrol',
      'carbon',
      'fuel',
      'motor',
      'gas natural',
      'gas',
      'gnl'
    ],
    color: '#607d8b'
  },
  { palabras: ['sin clasif', 'desconoc', 'no clasif', 'otra', 'otros'], color: '#9e9e9e' }
];

const normalizarTexto = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const colorParaTecnologia = (
  tecnologia: string,
  fallbackIndex = 0
): string => {
  if (!tecnologia) return COLOR_FALLBACK_TECNOLOGIA;
  const txt = normalizarTexto(tecnologia);
  for (const familia of FAMILIAS_TECNOLOGIA) {
    if (familia.palabras.some((p) => txt.includes(p))) {
      return familia.color;
    }
  }
  return PALETA_TECNOLOGIAS_FALLBACK[
    fallbackIndex % PALETA_TECNOLOGIAS_FALLBACK.length
  ];
};

export const construirColorPorTecnologia = (tecnologias: string[]): Map<string, string> => {
  const map = new Map<string, string>();
  let fallbackIdx = 0;
  for (const tec of tecnologias) {
    const txt = normalizarTexto(tec);
    const familia = FAMILIAS_TECNOLOGIA.find((f) => f.palabras.some((p) => txt.includes(p)));
    if (familia) {
      map.set(tec, familia.color);
    } else {
      map.set(
        tec,
        PALETA_TECNOLOGIAS_FALLBACK[fallbackIdx % PALETA_TECNOLOGIAS_FALLBACK.length]
      );
      fallbackIdx += 1;
    }
  }
  return map;
};

export const formatearCapacidad = (valor: number, unidad: 'MW' | 'kW'): string => {
  if (!Number.isFinite(valor)) return '—';
  const decimales = unidad === 'MW' ? 2 : 1;
  return `${valor.toLocaleString('es-CL', {
    maximumFractionDigits: decimales,
    minimumFractionDigits: decimales
  })} ${unidad}`;
};

export const formatearValorTreemap = (
  valor: number,
  unidad: 'MW' | 'kW' | 'proyectos'
): string => {
  if (!Number.isFinite(valor)) return '—';
  if (unidad === 'proyectos') {
    return `${Math.round(valor).toLocaleString('es-CL')} proy.`;
  }
  return formatearCapacidad(valor, unidad);
};
